import { slot } from "./dom.js";
import type { WordOrder } from "../segmenting/word-order.js";
import type { TieredElements } from "./tool-state.js";

/**
 * Finding the parts of the page the segmenter drives.
 *
 * Its own module beside `hanzi-elements.ts`, and the same all-or-nothing
 * contract: a template missing a slot yields nothing, and the tool declines to
 * start rather than half-working somewhere nobody looks.
 */

/** The orders the markup may offer, keyed by the value a button carries. */
const orders: ReadonlySet<string> = new Set([
  "count",
  "frequency",
  "appearance",
  "length",
  "band",
]);

/** Whether a string names one of the orders {@link WordOrder} allows. */
export function isWordOrder(value: string | undefined): value is WordOrder {
  return value !== undefined && orders.has(value);
}

/** One control a reader can order the list by. */
export interface OrderControl {
  /** The button itself. */
  readonly button: HTMLButtonElement;

  /**
   * The heading cell this button sits in, where it sits in one.
   *
   * `aria-sort` belongs to the column header and to nothing else, so a control
   * outside the table carries `aria-pressed` alone. Ordering by where a word
   * appears is one of those: the text decides it and no column shows it.
   */
  readonly heading: HTMLElement | undefined;

  readonly order: WordOrder;
}

/** Everything on the page this tool drives. */
export interface SegmenterElements extends TieredElements {
  /** Where the rows go. */
  readonly list: HTMLElement;

  /** How many words were found, and how many are distinct. */
  readonly summary: HTMLElement;

  /** What the page says when the text holds no Chinese. */
  readonly empty: HTMLElement;

  /** How much of the cap the input has used. */
  readonly counter: HTMLElement;

  /** The name applied as a tag to words saved from this text. */
  readonly tag: HTMLInputElement;

  /** Messages about saves that need the reader's attention. */
  readonly saveStatus: HTMLElement;

  /**
   * Where the icon sprite is, which the container carries.
   *
   * The save control on each row draws its two marks out of it, and a Lambda
   * or a script cannot know an address with a content hash in it. Absent
   * leaves those controls saying `Save` and `Saved` in words, which is the
   * table as it stood before the marks.
   */
  readonly icons: string | undefined;

  /** The column headings a reader can order by. */
  readonly ordering: readonly OrderControl[];

  readonly copy: HTMLButtonElement | undefined;
}

/** The ordering controls the markup carries, in the order it lists them. */
function orderControls(container: HTMLElement): readonly OrderControl[] {
  return [
    ...container.querySelectorAll<HTMLButtonElement>("button[data-cb-order]"),
  ].flatMap((button) => {
    const order = button.dataset["cbOrder"];

    if (!isWordOrder(order)) {
      return [];
    }

    return [{ button, heading: button.closest("th") ?? undefined, order }];
  });
}

/**
 * Find the tool's parts, or nothing where the template is missing one.
 *
 * The ordering controls are allowed to be absent one by one, and the list is
 * allowed to offer none at all. A heading that lost its button leaves a column
 * a reader cannot sort by, which is a smaller failure than a page with no word
 * list on it.
 */
export function findSegmenterElements(
  container: HTMLElement,
): SegmenterElements | undefined {
  const input = slot(container, "input");
  const list = slot(container, "list");
  const summary = slot(container, "summary");
  const empty = slot(container, "empty");
  const counter = slot(container, "counter");
  const status = slot(container, "status");
  const controls = slot(container, "controls");
  const tag = slot(container, "tag");
  const saveStatus = slot(container, "save-status");

  if (
    !(input instanceof HTMLTextAreaElement) ||
    list === undefined ||
    summary === undefined ||
    empty === undefined ||
    counter === undefined ||
    status === undefined ||
    controls === undefined ||
    !(tag instanceof HTMLInputElement) ||
    saveStatus === undefined
  ) {
    return undefined;
  }

  const copy = slot(container, "copy");
  const upgrade = slot(container, "upgrade");
  const revert = slot(container, "revert");

  return {
    icons: container.dataset["icons"],
    input,
    list,
    summary,
    empty,
    counter,
    tag,
    saveStatus,
    status,
    controls,
    ordering: orderControls(container),
    copy: copy instanceof HTMLButtonElement ? copy : undefined,
    upgrade: upgrade instanceof HTMLButtonElement ? upgrade : undefined,
    upgradePanel: slot(container, "upgrade-panel"),
    revert: revert instanceof HTMLButtonElement ? revert : undefined,
    revertPanel: slot(container, "revert-panel"),
  };
}
